import type { DocumentNamingOverride } from "./types";

import { cleanStringForFilesystem, prettyPrintDocumentType } from "../index";

/**
 * Dominion Energy (ENG-15522): `{Facility}_RY{YYYY}_{DocumentName}.{ext}`.
 *
 * - `Facility` keeps its natural spacing (e.g. `Harpers Backfeed Station`)
 *   per Dominion's request — only filesystem-unsafe chars are stripped.
 *   Other segments get the usual `cleanStringForFilesystem` treatment.
 * - `DocumentName` is the cleaned user-supplied name (filename or pre-existing
 *   title with facility/date noise stripped out by `deriveDocumentName`).
 * - When `DocumentName` is empty/blank, falls back to `prettyPrintDocumentType`.
 * - The `RY{YYYY}` segment is omitted when `reportingYear` is null.
 * - The `Facility` segment is omitted when `facilityName` is empty.
 */
export const dominionEnergyNamingOverride: DocumentNamingOverride = ({
  facilityName,
  reportingYear,
  documentType,
  documentName,
  fileExtension,
}) => {
  const facilitySegment = facilityName ? sanitizeFacilityNameForFilename(facilityName) : "";
  const rySegment = reportingYear ? `RY${reportingYear}` : "";
  const rawNameSegment = documentName?.trim() || prettyPrintDocumentType(documentType);
  const nameSegment = cleanStringForFilesystem(rawNameSegment);

  const joined = [facilitySegment, rySegment, nameSegment].filter((s) => s.length > 0).join("_");
  return fileExtension ? `${joined}.${fileExtension}` : joined;
};

/**
 * Strip only characters that aren't safe on common filesystems
 * (`/`, `\`, `:`, `*`, `?`, `"`, `<`, `>`, `|`, control chars) and collapse
 * whitespace. Spaces are preserved so `Harpers Backfeed Station` stays
 * readable instead of becoming `Harpers_Backfeed_Station`.
 */
function sanitizeFacilityNameForFilename(name: string): string {
  // eslint-disable-next-line no-control-regex
  const unsafeChars = /[/\\:*?"<>|\x00-\x1f]/g;
  return name.replace(unsafeChars, "").replace(/\s+/g, " ").trim();
}

export const dominionEnergyNamingFormatHelpText =
  "FacilityName_RY{Year}_DocumentName.fileExtension";
